import { Form, useLoaderData, useNavigation, useActionData, useSubmit } from 'react-router'
import type { ActionFunctionArgs } from 'react-router'
import { redirect } from 'react-router'
import { db } from '@afficheservices/db'
import { serviceCategories, leads } from '@afficheservices/db/schema'
import { isNull, gte, sql } from 'drizzle-orm'
import { checkRateLimit } from '~/lib/security.server'
import { useEffect, useRef, useState } from 'react'

// Visual styling for top-of-form "Services les plus demandés" tiles.
// Keyed by category slug; safe fallback used for unknown slugs.
// Matches the tile color/emoji pattern used in /devis-gratuit (SERVICES const).
const POPULAR_TILE_STYLE: Record<string, { emoji: string; color: string }> = {
  plomberie:    { emoji: '🔧', color: 'border-blue-200 bg-blue-50 text-blue-700 hover:border-blue-400 hover:bg-blue-100' },
  electricite:  { emoji: '⚡', color: 'border-amber-200 bg-amber-50 text-amber-700 hover:border-amber-400 hover:bg-amber-100' },
  peinture:     { emoji: '🎨', color: 'border-purple-200 bg-purple-50 text-purple-700 hover:border-purple-400 hover:bg-purple-100' },
  renovation:   { emoji: '🏗️', color: 'border-orange-200 bg-orange-50 text-orange-700 hover:border-orange-400 hover:bg-orange-100' },
  menuiserie:   { emoji: '🪚', color: 'border-yellow-200 bg-yellow-50 text-yellow-700 hover:border-yellow-400 hover:bg-yellow-100' },
  toiture:      { emoji: '🏠', color: 'border-red-200 bg-red-50 text-red-700 hover:border-red-400 hover:bg-red-100' },
  chauffage:    { emoji: '🔥', color: 'border-rose-200 bg-rose-50 text-rose-700 hover:border-rose-400 hover:bg-rose-100' },
  serrurerie:   { emoji: '🔑', color: 'border-slate-200 bg-slate-50 text-slate-700 hover:border-slate-400 hover:bg-slate-100' },
  carrelage:    { emoji: '🧱', color: 'border-teal-200 bg-teal-50 text-teal-700 hover:border-teal-400 hover:bg-teal-100' },
  jardinage:    { emoji: '🌿', color: 'border-green-200 bg-green-50 text-green-700 hover:border-green-400 hover:bg-green-100' },
}
const POPULAR_TILE_FALLBACK = { emoji: '🛠️', color: 'border-slate-200 bg-slate-50 text-slate-700 hover:border-slate-400 hover:bg-slate-100' }

export async function loader() {
  // All top-level categories — long-tail list shown below the tiles.
  const categories = await db
    .select({ id: serviceCategories.id, name: serviceCategories.nameFr, slug: serviceCategories.slug })
    .from(serviceCategories)
    .where(isNull(serviceCategories.parentId))
    .orderBy(serviceCategories.displayOrder)

  // Popular services = top 6 by submitted lead count over last 30d.
  // Cutoff computed here (server) — never in render body (hydration #418 risk).
  const cutoff = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
  const popularRows = await db
    .select({
      categoryId: leads.categoryId,
      cnt: sql<number>`count(*)::int`,
    })
    .from(leads)
    .where(gte(leads.createdAt, cutoff))
    .groupBy(leads.categoryId)
    .orderBy(sql`count(*) desc`)
    .limit(20)

  const countByCat = new Map(popularRows.map((r) => [r.categoryId, r.cnt]))
  // Order categories by lead count desc; fall back to displayOrder when no leads yet.
  const ranked = [...categories].sort((a, b) => {
    const ca = countByCat.get(a.id) ?? 0
    const cb = countByCat.get(b.id) ?? 0
    if (cb !== ca) return cb - ca
    return 0
  })
  const popular = ranked.slice(0, 6)

  return { categories, popular }
}

export async function action({ request }: ActionFunctionArgs) {
  // Multi-step funnel — soft cap on the entry point only (60/min via the
  // 'api' bucket). The previous 'form' bucket allowed only 3 hits/min,
  // which 429-blocked normal users navigating between steps and broke
  // the action JSON shape (front-end was rendering an object → React #31).
  if (!checkRateLimit(request, 'api')) {
    return { error: 'Trop de tentatives. Réessayez dans une minute.' }
  }
  const formData = await request.formData()
  const categoryId = formData.get('categoryId') as string
  if (!categoryId) return { error: 'Veuillez sélectionner un service' }
  return redirect(`/demande-devis/2?cat=${categoryId}`)
}

export default function Step1() {
  const { categories, popular } = useLoaderData<typeof loader>()
  const actionData = useActionData<typeof action>()
  const navigation = useNavigation()
  const submit = useSubmit()
  const formRef = useRef<HTMLFormElement>(null)
  const [selected, setSelected] = useState('')
  const [search, setSearch] = useState('')

  // Auto-advance 350ms after category pick — Typeform/Doctolib pattern.
  // WCAG 3.2.2: advised via "Choisissez pour continuer →" hint below H1.
  // Cleanup clears the timer if user changes selection within the window.
  useEffect(() => {
    if (!selected || !formRef.current) return
    const t = setTimeout(() => {
      if (formRef.current) submit(formRef.current, { method: 'post' })
    }, 350)
    return () => clearTimeout(t)
  }, [selected, submit])

  // When user types in search, hide popular tiles + scope list to matches.
  const isSearching = search.length > 0
  const filtered = isSearching
    ? categories.filter((c) => c.name.toLowerCase().includes(search.toLowerCase()))
    : categories

  return (
    <div>
      <h1 className="text-center text-2xl font-bold text-slate-900 sm:text-3xl">
        Quel service recherchez-vous ?
      </h1>
      <p className="mt-3 text-center text-sm text-slate-600 sm:text-base">
        Recevez 3 devis gratuits en 48h — choisissez votre besoin (30 sec).
      </p>
      <p className="mt-1 text-center text-xs text-slate-400">
        Choisissez pour continuer →
      </p>

      {actionData?.error && (
        <p className="mt-4 text-center text-sm text-red-500">{actionData.error}</p>
      )}

      <Form ref={formRef} method="post" className="mt-6">
        <input type="hidden" name="categoryId" value={selected} />

        {/* Popular services — tiles. Hidden once user types in search. */}
        {!isSearching && popular.length > 0 && (
          <div className="mb-5">
            <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
              Services les plus demandés
            </p>
            <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3">
              {popular.map((cat) => {
                const style = POPULAR_TILE_STYLE[cat.slug] ?? POPULAR_TILE_FALLBACK
                const isActive = selected === cat.id
                return (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => setSelected(cat.id)}
                    className={`flex flex-col items-center gap-1.5 rounded-xl border-2 px-3 py-4 text-center transition-all btn-press ${
                      isActive
                        ? 'border-primary bg-primary-50 text-primary-700 shadow-sm'
                        : style.color
                    }`}
                    aria-pressed={isActive}
                  >
                    <span className="text-2xl leading-none" aria-hidden="true">{style.emoji}</span>
                    <span className="text-sm font-semibold leading-tight">{cat.name}</span>
                  </button>
                )
              })}
            </div>
          </div>
        )}

        {/* Quick search */}
        <input
          type="text"
          placeholder={isSearching ? 'Rechercher un service...' : 'Autre service ? Rechercher...'}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="mb-3 block w-full rounded-xl border border-slate-200 px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
        />

        <div className="max-h-[400px] overflow-y-auto overflow-hidden rounded-xl border border-slate-200">
          {filtered.length === 0 && (
            <p className="px-5 py-8 text-center text-sm text-slate-400">Aucun service trouvé</p>
          )}
          {filtered.map((cat, i) => (
            <label
              key={cat.id}
              className={`flex cursor-pointer items-center gap-4 px-5 py-4 transition-colors ${
                i > 0 ? 'border-t border-slate-100' : ''
              } ${selected === cat.id ? 'bg-primary-50' : 'hover:bg-slate-50'}`}
            >
              <div className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors ${
                selected === cat.id ? 'border-primary' : 'border-slate-300'
              }`}>
                {selected === cat.id && <div className="h-2.5 w-2.5 rounded-full bg-primary" />}
              </div>
              <input type="radio" name="r" value={cat.id} checked={selected === cat.id}
                onChange={() => setSelected(cat.id)} className="sr-only" />
              <span className="text-sm font-medium text-slate-900">{cat.name}</span>
            </label>
          ))}
        </div>

        {/* Sticky CTA — always thumb-reachable on mobile. iOS safe-area aware.
            Renders in-flow on desktop too; sticky bottom-0 keeps it pinned
            when the category list overflows. */}
        <div
          className="sticky bottom-0 z-20 -mx-4 mt-6 border-t border-slate-100 bg-white/95 px-4 py-3 backdrop-blur"
          style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
        >
          <button type="submit" disabled={!selected || navigation.state === 'submitting'}
            className="flex min-h-14 w-full items-center justify-center gap-2 rounded-xl bg-primary py-3.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-primary-hover disabled:opacity-40 btn-press">
            {navigation.state === 'submitting' ? (
              <><svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg> Chargement...</>
            ) : 'Suivant'}
          </button>
        </div>
      </Form>
    </div>
  )
}
